// SPDX-License-Identifier: AGPL-3.0-only

import { requestJson } from "./http.js";

export type FirmwareChannel = "stable" | "beta";
export type FirmwareUpdateMode = "manual" | "automatic" | "disabled";
export type FirmwareReleaseStatus = "draft" | "published" | "revoked";

export interface FirmwareRelease {
  readonly id: string;
  readonly version: string;
  readonly channel: FirmwareChannel;
  readonly probeType: string;
  readonly status: FirmwareReleaseStatus;
  readonly artifactUrl: string;
  readonly sizeBytes: number;
  readonly sha256: string;
  readonly mandatory: boolean;
  readonly signature: string | null;
  readonly releaseNotes: string | null;
  readonly createdAt: string;
  readonly publishedAt: string | null;
  readonly revokedAt: string | null;
}

export interface CreateFirmwareReleaseInput {
  readonly channel: FirmwareChannel;
  readonly probeType: string;
  readonly artifactFile: File;
  readonly mandatory: boolean;
  readonly signature: string | null;
  readonly releaseNotes: string | null;
}

export interface FirmwareProbeType {
  readonly id: string;
  readonly displayName: string;
  readonly platform: string;
  readonly hardwareModel: string;
  readonly architecture: string;
}

export interface TenantFirmwarePolicy {
  readonly tenantId: string;
  readonly defaultChannel: FirmwareChannel;
  readonly defaultUpdateMode: FirmwareUpdateMode;
  readonly updatedAt: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(value: unknown, fieldName: string): string {
  if (typeof value !== "string") {
    throw new Error(`Firmware response field ${fieldName} is invalid`);
  }
  return value;
}

function readNullableString(value: unknown, fieldName: string): string | null {
  if (value === null) {
    return null;
  }
  return readString(value, fieldName);
}

function readNumber(value: unknown, fieldName: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`Firmware response field ${fieldName} is invalid`);
  }
  return value;
}

function readChannel(value: unknown, fieldName: string): FirmwareChannel {
  if (value !== "stable" && value !== "beta") {
    throw new Error(`Firmware response field ${fieldName} is invalid`);
  }
  return value;
}

function readUpdateMode(value: unknown, fieldName: string): FirmwareUpdateMode {
  if (value !== "manual" && value !== "automatic" && value !== "disabled") {
    throw new Error(`Firmware response field ${fieldName} is invalid`);
  }
  return value;
}

function readReleaseStatus(value: unknown, fieldName: string): FirmwareReleaseStatus {
  if (value !== "draft" && value !== "published" && value !== "revoked") {
    throw new Error(`Firmware response field ${fieldName} is invalid`);
  }
  return value;
}

function readFirmwareRelease(payload: unknown, fieldName: string): FirmwareRelease {
  if (!isRecord(payload)) {
    throw new Error(`Firmware response field ${fieldName} is invalid`);
  }
  return {
    id: readString(payload.id, `${fieldName}.id`),
    version: readString(payload.version, `${fieldName}.version`),
    channel: readChannel(payload.channel, `${fieldName}.channel`),
    probeType: readString(payload.probeType, `${fieldName}.probeType`),
    status: readReleaseStatus(payload.status, `${fieldName}.status`),
    artifactUrl: readString(payload.artifactUrl, `${fieldName}.artifactUrl`),
    sizeBytes: readNumber(payload.sizeBytes, `${fieldName}.sizeBytes`),
    sha256: readString(payload.sha256, `${fieldName}.sha256`),
    mandatory: typeof payload.mandatory === "boolean" ? payload.mandatory : false,
    signature: readNullableString(payload.signature, `${fieldName}.signature`),
    releaseNotes: readNullableString(payload.releaseNotes, `${fieldName}.releaseNotes`),
    createdAt: readString(payload.createdAt, `${fieldName}.createdAt`),
    publishedAt: readNullableString(payload.publishedAt, `${fieldName}.publishedAt`),
    revokedAt: readNullableString(payload.revokedAt, `${fieldName}.revokedAt`),
  };
}

function readFirmwareProbeType(payload: unknown, fieldName: string): FirmwareProbeType {
  if (!isRecord(payload)) throw new Error(`Firmware probe type ${fieldName} is invalid`);
  return { id: readString(payload.id, `${fieldName}.id`), displayName: readString(payload.displayName, `${fieldName}.displayName`), platform: readString(payload.platform, `${fieldName}.platform`), hardwareModel: readString(payload.hardwareModel, `${fieldName}.hardwareModel`), architecture: readString(payload.architecture, `${fieldName}.architecture`) };
}

function readTenantFirmwarePolicy(payload: unknown): TenantFirmwarePolicy {
  if (!isRecord(payload)) {
    throw new Error("Firmware policy response is invalid");
  }
  return {
    tenantId: readString(payload.tenantId, "tenantId"),
    defaultChannel: readChannel(payload.defaultChannel, "defaultChannel"),
    defaultUpdateMode: readUpdateMode(payload.defaultUpdateMode, "defaultUpdateMode"),
    updatedAt: readString(payload.updatedAt, "updatedAt"),
  };
}

function normalizeOptionalText(value: string): string | null {
  const trimmedValue: string = value.trim();
  return trimmedValue === "" ? null : trimmedValue;
}

export async function listFirmwareReleases(): Promise<readonly FirmwareRelease[]> {
  const payload: unknown = await requestJson("/api/v1/system/firmware/releases");
  if (!isRecord(payload) || !Array.isArray(payload.releases)) {
    throw new Error("Firmware release list response is invalid");
  }
  return payload.releases.map((release: unknown, index: number): FirmwareRelease =>
    readFirmwareRelease(release, `releases[${index}]`),
  );
}

export async function listFirmwareProbeTypes(): Promise<readonly FirmwareProbeType[]> {
  const payload: unknown = await requestJson("/api/v1/system/firmware/probe-types");
  if (!isRecord(payload) || !Array.isArray(payload.probeTypes)) throw new Error("Firmware probe type list response is invalid");
  return payload.probeTypes.map((probeType: unknown, index: number): FirmwareProbeType => readFirmwareProbeType(probeType, `probeTypes[${index}]`));
}

export async function createFirmwareRelease(input: CreateFirmwareReleaseInput): Promise<FirmwareRelease> {
  const query: URLSearchParams = new URLSearchParams();
  query.set("channel", input.channel);
  query.set("probeType", input.probeType);
  query.set("mandatory", input.mandatory ? "true" : "false");
  if (input.signature !== null) {
    query.set("signature", input.signature);
  }
  if (input.releaseNotes !== null) {
    query.set("releaseNotes", input.releaseNotes);
  }

  const payload: unknown = await requestJson(`/api/v1/system/firmware/releases?${query.toString()}`, {
    method: "POST",
    body: input.artifactFile,
    headers: {
      "Content-Type": "application/octet-stream",
    },
  });
  return readFirmwareRelease(payload, "release");
}

export async function publishFirmwareRelease(releaseId: string): Promise<FirmwareRelease> {
  const payload: unknown = await requestJson(
    `/api/v1/system/firmware/releases/${encodeURIComponent(releaseId)}/publish`,
    { method: "POST" },
  );
  return readFirmwareRelease(payload, "release");
}

export async function revokeFirmwareRelease(releaseId: string): Promise<FirmwareRelease> {
  const payload: unknown = await requestJson(
    `/api/v1/system/firmware/releases/${encodeURIComponent(releaseId)}/revoke`,
    { method: "POST" },
  );
  return readFirmwareRelease(payload, "release");
}

export async function getTenantFirmwarePolicy(tenantId: string): Promise<TenantFirmwarePolicy> {
  const payload: unknown = await requestJson(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/firmware-policy`,
  );
  return readTenantFirmwarePolicy(payload);
}

export async function updateTenantFirmwarePolicy(
  tenantId: string,
  defaultChannel: FirmwareChannel,
  defaultUpdateMode: FirmwareUpdateMode,
): Promise<TenantFirmwarePolicy> {
  const payload: unknown = await requestJson(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/firmware-policy`,
    {
      method: "PUT",
      body: JSON.stringify({ defaultChannel, defaultUpdateMode }),
    },
  );
  return readTenantFirmwarePolicy(payload);
}

export function firmwareReleaseInputFromStrings(input: {
  readonly channel: FirmwareChannel;
  readonly probeType: string;
  readonly artifactFile: File;
  readonly mandatory: boolean;
  readonly signature: string;
  readonly releaseNotes: string;
}): CreateFirmwareReleaseInput {
  return {
    channel: input.channel,
    probeType: input.probeType,
    artifactFile: input.artifactFile,
    mandatory: input.mandatory,
    signature: normalizeOptionalText(input.signature),
    releaseNotes: normalizeOptionalText(input.releaseNotes),
  };
}
